import { put, race, take } from 'redux-saga/effects';

import { SettingsNationalHolidays } from '../domain/SettingsNationalHolidays';

import {
  SettingsNationalHolidaysActionCreators,
  SettingsNationalHolidaysActionTypes,
  settingsNationalHolidaysSelector,
} from '../redux/server/settingsNationalHolidays';
import { ComparativeSection } from '../utils/comparativeSectionUtil';
import { customSelect } from '../utils/customSelect';

/**
 * 祝日取得の結果
 * success または failure のどちらかがセットされる
 */
type FetchNationalHolidaysRaceResult = {
  success?: unknown;
  failure?: unknown;
};

/** この関数の戻り値（祝日データまたは未取得） */
type EnsureNationalHolidaysLoadedReturn = SettingsNationalHolidays | undefined;

/**
 * 祝日データが必要な場合に取得し、取得済みの祝日データを返す
 * 比較区分が「平日/土日祝比較」でない場合、または既に祝日データが存在する場合は即座に返す
 * 取得に失敗した場合はundefinedを返し、呼び出し側で処理を中断する
 *
 * @param comparativeSection 比較区分
 * @returns 祝日データ（取得失敗時はundefined）
 */
export function* ensureNationalHolidaysLoaded(
  comparativeSection?: ComparativeSection | undefined
): Generator<
  unknown,
  EnsureNationalHolidaysLoadedReturn,
  EnsureNationalHolidaysLoadedReturn
> {
  // 比較区分が「平日/土日祝比較」でない場合は、既存の祝日データを返す（存在しない場合はundefined）
  if (comparativeSection !== '平日/土日祝比較') {
    return yield* customSelect(settingsNationalHolidaysSelector);
  }

  // 既に祝日データが存在する場合は即座に返す
  const nationalHolidays = yield* customSelect(
    settingsNationalHolidaysSelector
  );
  if (nationalHolidays != null) {
    return nationalHolidays;
  }

  // 祝日データを取得する
  yield put(
    SettingsNationalHolidaysActionCreators.fetchSettingsNationalHolidaysAction()
  );

  // 成功または失敗のどちらか早い方を待つ
  // 失敗時はsettingNationalHolidaysSagaでhandleErrorSagaが呼ばれ、エラーメッセージが表示される
  // Generatorのnext型が統一されているため、このyieldで受け取るraceの戻りだけ型を明示する
  const result = (yield race({
    success: take(
      SettingsNationalHolidaysActionTypes.FETCH_SETTINGS_NATIONAL_HOLIDAYS_SUCCESS
    ),
    failure: take(
      SettingsNationalHolidaysActionTypes.RENEW_SETTINGS_NATIONAL_HOLIDAYS
    ),
  })) as FetchNationalHolidaysRaceResult;

  // 失敗時はundefinedを返し、呼び出し側で処理を中断する
  // searchConditionToDateRangeParamsは祝日データがnullの場合にエラーをスローするため、
  // 祝日データが取得できない場合は処理を続行できない
  if (result.failure) {
    return undefined;
  }

  // 取得成功後、最新の祝日データを返す
  return yield* customSelect(settingsNationalHolidaysSelector);
}

/**
 * 祝日データが必要な場合に取得し、取得結果と処理を続行して良いかを返す
 * 比較区分が「平日/土日祝比較」で取得に失敗した場合は、呼び出し側で処理を中断する
 *
 * @param comparativeSection 比較区分
 * @returns nationalHolidays: 取得済み祝日（不要 or 未取得時は undefined）
 *          shouldAbort: true のときは呼び出し側で return すること
 */
export function* ensureNationalHolidaysWhenNeeded(
  comparativeSection?: ComparativeSection | undefined
): Generator<
  unknown,
  {
    nationalHolidays: SettingsNationalHolidays | undefined;
    shouldAbort: boolean;
  },
  EnsureNationalHolidaysLoadedReturn
> {
  const nationalHolidays = yield* ensureNationalHolidaysLoaded(
    comparativeSection
  );
  const shouldAbort =
    nationalHolidays == null && comparativeSection === '平日/土日祝比較';
  return { nationalHolidays, shouldAbort };
}
